import { type ConnectNoun, localize } from "@ando/ui";
import { Trans, useLingui } from "@lingui/react/macro";
import { AlertCircle, Loader2 } from "lucide-react";
import type * as React from "react";

import type { BenefitBullet } from "@ando/ui";

/**
 * Small presentational helpers shared by the per-capability page:
 *
 *   • ``computeStatus`` — pure derivation from the connections list
 *   • ``StatusPill``   — the count pill rendered in the page header
 *   • ``BenefitsGrid`` — the bottom "Vantaggi" 2-up / 4-up grid
 *   • ``ConnectionsListSkeleton`` — top-loading shimmer
 *   • ``ErrorState``   — list-load error w/ "Riprova" CTA
 *
 * Extracted from ``$capability.tsx`` on May 28, 2026. Every render output
 * is byte-equivalent to the inline originals.
 */

export type ConnectionStatus = "healthy" | "needs_reauth" | "empty";

export function computeStatus(connections: readonly { token_status: string }[]): ConnectionStatus {
  if (connections.length === 0) return "empty";
  if (connections.some((c) => c.token_status === "needs_reauth")) return "needs_reauth";
  return "healthy";
}

export const StatusPill: React.FC<{
  status: ConnectionStatus;
  count: number;
  noun: ConnectNoun;
}> = ({ status, count, noun }) => {
  if (status === "empty") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-ink-3/10 px-2 py-0.5 font-medium text-[10.5px] text-ink-3">
        <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-ink-3/50" />
        {noun.emptyLabel}
      </span>
    );
  }
  if (status === "needs_reauth") {
    const nounWord = count === 1 ? noun.singular : noun.plural;
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-warning/15 px-2 py-0.5 font-medium text-[10.5px] text-warning">
        <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-warning" />
        <Trans>
          {count} {nounWord} · riconnessione richiesta
        </Trans>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2 py-0.5 font-medium text-[10.5px] text-success">
      <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-success" />
      {noun.countLabel(count)}
    </span>
  );
};
StatusPill.displayName = "StatusPill";

export const BenefitsGrid: React.FC<{
  benefits: readonly BenefitBullet[];
}> = ({ benefits }) => {
  const { i18n } = useLingui();
  return (
    <section className="flex flex-col gap-2">
      <header className="flex items-center gap-2">
        <h3 className="font-semibold text-[10.5px] text-ink-2 uppercase tracking-[0.08em]">
          <Trans>Vantaggi</Trans>
        </h3>
      </header>
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {benefits.map((b) => {
          const title = localize(b.title, i18n);
          const body = localize(b.body, i18n);
          return (
            <li
              key={title}
              className="flex items-start gap-2 rounded-lg border border-hairline bg-cream/40 px-3 py-2"
            >
              <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md bg-ando-orange-soft text-ando-orange">
                {b.icon}
              </span>
              <div className="min-w-0">
                <p className="font-medium text-[12px] text-ink-1">{title}</p>
                <p className="text-[11px] text-ink-3 leading-relaxed">{body}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
BenefitsGrid.displayName = "BenefitsGrid";

export const ConnectionsListSkeleton: React.FC = () => (
  <section className="flex flex-col gap-2" aria-busy>
    {/* Header mirrors the de-rainbowed "Account collegati" section: a
        low-salience dot + label, no coloured left border. */}
    <header className="flex items-center gap-2">
      <div className="h-3 w-32 animate-pulse rounded bg-ink-3/15" />
    </header>
    <ul className="flex flex-col gap-1.5">
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          className="flex items-center gap-3 rounded-lg border border-hairline bg-card/60 px-3 py-2.5"
        >
          <div className="h-11 w-11 shrink-0 animate-pulse rounded-md bg-ink-3/15" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="h-3 w-40 animate-pulse rounded bg-ink-3/15" />
            <div className="h-2.5 w-52 animate-pulse rounded bg-ink-3/10" />
          </div>
          {/* Right-side action affordances the real row carries (Gestisci → + kebab). */}
          <div className="h-5 w-16 shrink-0 animate-pulse rounded-md bg-ink-3/10" />
          <div className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-ink-3/10" />
        </li>
      ))}
    </ul>
  </section>
);
ConnectionsListSkeleton.displayName = "ConnectionsListSkeleton";

export const ErrorState: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <section className="flex flex-col items-center gap-3 rounded-xl border border-warning/30 bg-warning/[0.06] px-6 py-8 text-center">
    <AlertCircle className="h-7 w-7 text-warning" strokeWidth={1.75} aria-hidden />
    <div>
      <h3 className="font-semibold text-[13px] text-ink-1">
        <Trans>Impossibile caricare gli account</Trans>
      </h3>
      <p className="mt-1 text-[12px] text-ink-3">
        <Trans>Controlla la connessione e riprova.</Trans>
      </p>
    </div>
    <button
      type="button"
      onClick={onRetry}
      className="inline-flex items-center gap-1.5 rounded-md bg-warning/20 px-3 py-1.5 font-medium text-[12px] text-warning transition-colors hover:bg-warning/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning"
    >
      <Loader2 size={12} strokeWidth={2} aria-hidden />
      <Trans>Riprova</Trans>
    </button>
  </section>
);
ErrorState.displayName = "ErrorState";
